import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Tag } from './TagsStore';

// SubTask数据结构
export interface SubTask {
  id: string;
  content: string;
  completed: boolean;
  createdAt: Date;
}

export interface Task {
  id: string;
  title: string;
  completed: boolean;
  createdAt: Date;
  completedAt?: Date;
  tags: Tag[]; // <--- Changed from string[] to Tag[]
  description?: string; // 可选的任务描述
  priority?: 'low' | 'medium' | 'high'; // 优先级
  dueDate?: Date; // 可选的截止日期
  estimatedPomodoros?: number; // 预估番茄钟数量
  actualPomodoros?: number; // 实际使用的番茄钟数量
  isPinned?: boolean; // 是否置顶
  order?: number; // 排序字段
  subTasks?: SubTask[]; // 子任务列表
  comment?: string; // 任务备注
}

interface TaskState {
  tasks: Task[];
  completedTasks: Task[];
  taskHistory: Array<{ date: string; count: number }>; // 任务完成历史记录

  // 基本任务操作
  addTask: (title: string, tags?: Tag[], options?: Partial<Pick<Task, 'description' | 'priority' | 'dueDate' | 'estimatedPomodoros'>>) => Task;
  updateTask: (id: string, updates: Partial<Task>) => void;
  deleteTask: (id: string) => void;
  toggleTask: (id: string) => void;

  // 任务查询
  getTaskById: (id: string) => Task | undefined;
  getActiveTasks: () => Task[];
  getCompletedTasks: () => Task[];
  getTasksByTag: (tagId: string) => Task[];
  getTasksByPriority: (priority: Task['priority']) => Task[];

  // 统计信息
  getTotalTasks: () => number;
  getCompletedTasksCount: () => number;
  getTaskCompletionRate: () => number;

  // 高级功能
  searchTasks: (query: string) => Task[];
  getTasksDueToday: () => Task[];
  getOverdueTasks: () => Task[];

  // 番茄钟相关
  incrementPomodoro: (taskId: string) => void;

  // 置顶和排序功能
  toggleTaskPin: (taskId: string) => void;
  reorderTasks: (taskIds: string[]) => void;

  // 批量操作
  markAllCompleted: () => void;
  deleteCompletedTasks: () => void;

  // 数据管理
  clearAllTasks: () => void;
  exportTasks: () => string;
  importTasks: (data: string) => void;

  // 初始化示例数据
  initializeSampleData: () => void;
}

export const useTaskStore = create<TaskState>()(
  persist(
    (set, get) => ({
      tasks: [],
      completedTasks: [],
      taskHistory: [], // 初始化任务历史记录

      // 基本任务操作
      addTask: (title: string, tags: Tag[] = [], options = {}) => {
        const state = get();
        const maxOrder = Math.max(...state.tasks.map(t => t.order ?? 0), -1);

        const newTask: Task = {
          id: `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          title: title.trim(),
          completed: false,
          createdAt: new Date(),
          tags,
          isPinned: false,
          order: maxOrder + 1,
          ...options,
        };

        set(state => ({
          tasks: [...state.tasks, newTask]
        }));

        return newTask;
      },

      updateTask: (id: string, updates: Partial<Task>) => {
        set(state => ({
          tasks: state.tasks.map(task =>
            task.id === id ? { ...task, ...updates } : task
          ),
          completedTasks: state.completedTasks.map(task =>
            task.id === id ? { ...task, ...updates } : task
          )
        }));
      },

      deleteTask: (id: string) => {
        set(state => ({
          tasks: state.tasks.filter(task => task.id !== id),
          completedTasks: state.completedTasks.filter(task => task.id !== id)
        }));
      },

      toggleTask: (id: string) => {
        const task = get().getTaskById(id);
        if (!task) return;

        if (task.completed) {
          // 将任务从已完成移回待完成
          const updatedTask = {
            ...task,
            completed: false,
            completedAt: undefined
          };

          set(state => ({
            tasks: [...state.tasks, updatedTask],
            completedTasks: state.completedTasks.filter(t => t.id !== id)
          }));
        } else {
          // 将任务标记为已完成
          const completedTask = {
            ...task,
            completed: true,
            completedAt: new Date()
          };

          // 更新任务历史记录
          const today = new Date().toISOString().slice(0, 10);
          const currentHistory = get().taskHistory;
          const existingEntry = currentHistory.find(entry => entry.date === today);

          let newHistory;
          if (existingEntry) {
            newHistory = currentHistory.map(entry =>
              entry.date === today
                ? { ...entry, count: entry.count + 1 }
                : entry
            );
          } else {
            newHistory = [...currentHistory, { date: today, count: 1 }];
          }

          set(state => ({
            tasks: state.tasks.filter(t => t.id !== id),
            completedTasks: [...state.completedTasks, completedTask],
            taskHistory: newHistory
          }));
        }
      },

      // 任务查询
      getTaskById: (id: string) => {
        const state = get();
        return state.tasks.find(task => task.id === id) ||
          state.completedTasks.find(task => task.id === id);
      },

      getActiveTasks: () => {
        return get().tasks
          .filter(task => !task.completed)
          .sort((a, b) => {
            // 首先按置顶状态排序 (置顶的在前)
            if (a.isPinned && !b.isPinned) return -1;
            if (!a.isPinned && b.isPinned) return 1;

            // 然后按order字段排序
            const orderA = a.order ?? Infinity;
            const orderB = b.order ?? Infinity;
            if (orderA !== orderB) return orderA - orderB;

            // 最后按创建时间排序 (新的在前)
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          });
      },

      getCompletedTasks: () => {
        return get().completedTasks;
      },

      getTasksByTag: (tagId: string) => {
        const state = get();
        return [...state.tasks, ...state.completedTasks].filter(task =>
          task.tags.some(tag => tag.id === tagId)
        );
      },

      getTasksByPriority: (priority: Task['priority']) => {
        const state = get();
        return [...state.tasks, ...state.completedTasks].filter(task =>
          task.priority === priority
        );
      },

      // 统计信息
      getTotalTasks: () => {
        const state = get();
        return state.tasks.length + state.completedTasks.length;
      },

      getCompletedTasksCount: () => {
        return get().completedTasks.length;
      },

      getTaskCompletionRate: () => {
        const state = get();
        const total = state.tasks.length + state.completedTasks.length;
        if (total === 0) return 0;
        return (state.completedTasks.length / total) * 100;
      },

      // 高级功能
      searchTasks: (query: string) => {
        const state = get();
        const searchTerm = query.toLowerCase().trim();
        if (!searchTerm) return [];

        return [...state.tasks, ...state.completedTasks].filter(task =>
          task.title.toLowerCase().includes(searchTerm) ||
          (task.description && task.description.toLowerCase().includes(searchTerm))
        );
      },

      getTasksDueToday: () => {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        const state = get();

        return state.tasks.filter(task =>
          task.dueDate && new Date(task.dueDate) <= today
        );
      },

      getOverdueTasks: () => {
        const now = new Date();
        const state = get();

        return state.tasks.filter(task =>
          task.dueDate && new Date(task.dueDate) < now && !task.completed
        );
      },

      // 番茄钟相关
      incrementPomodoro: (taskId: string) => {
        set(state => ({
          tasks: state.tasks.map(task =>
            task.id === taskId
              ? { ...task, actualPomodoros: (task.actualPomodoros || 0) + 1 }
              : task
          ),
          completedTasks: state.completedTasks.map(task =>
            task.id === taskId
              ? { ...task, actualPomodoros: (task.actualPomodoros || 0) + 1 }
              : task
          )
        }));
      },

      // 置顶和排序功能
      toggleTaskPin: (taskId: string) => {
        set(state => ({
          tasks: state.tasks.map(task =>
            task.id === taskId
              ? { ...task, isPinned: !task.isPinned }
              : task
          )
        }));
      },

      reorderTasks: (taskIds: string[]) => {
        set(state => {
          const taskMap = new Map(state.tasks.map(task => [task.id, task]));
          const reorderedTasks = taskIds
            .map(id => taskMap.get(id))
            .filter((task): task is Task => task !== undefined)
            .map((task, index) => ({ ...task, order: index }));

          // 添加未在重排序列表中的任务
          const remainingTasks = state.tasks
            .filter(task => !taskIds.includes(task.id))
            .map((task, index) => ({ ...task, order: reorderedTasks.length + index }));

          return {
            tasks: [...reorderedTasks, ...remainingTasks]
          };
        });
      },

      // 批量操作
      markAllCompleted: () => {
        const state = get();
        const now = new Date();
        const completedTasks = state.tasks.map(task => ({
          ...task,
          completed: true,
          completedAt: now
        }));

        set({
          tasks: [],
          completedTasks: [...state.completedTasks, ...completedTasks]
        });
      },

      deleteCompletedTasks: () => {
        set({
          completedTasks: []
        });
      },

      // 数据管理
      clearAllTasks: () => {
        set({
          tasks: [],
          completedTasks: []
        });
      },

      exportTasks: () => {
        const state = get();
        return JSON.stringify({
          tasks: state.tasks,
          completedTasks: state.completedTasks,
          exportDate: new Date().toISOString()
        }, null, 2);
      },

      importTasks: (data: string) => {
        try {
          const parsed = JSON.parse(data);
          if (parsed.tasks && parsed.completedTasks) {
            // 转换日期字符串回Date对象
            const convertDates = (tasks: Task[]) => tasks.map(task => ({
              ...task,
              createdAt: new Date(task.createdAt),
              completedAt: task.completedAt ? new Date(task.completedAt) : undefined,
              dueDate: task.dueDate ? new Date(task.dueDate) : undefined,
              tags: task.tags.map(tag => ({ ...tag, createdAt: new Date(tag.createdAt) }))
            }));

            set({
              tasks: convertDates(parsed.tasks),
              completedTasks: convertDates(parsed.completedTasks)
            });
          }
        } catch (error) {
          console.error('Failed to import tasks:', error);
        }
      },

      // 初始化示例数据
      initializeSampleData: () => {
        const state = get();
        // 只有在没有任务时才添加示例数据
        if (state.tasks.length === 0 && state.completedTasks.length === 0) {
          const sampleTasks: Task[] = [
            {
              id: 'sample_1',
              title: '完成番茄钟应用开发',
              completed: false,
              createdAt: new Date(),
              tags: [],
              priority: 'high',
              estimatedPomodoros: 4
            },
            {
              id: 'sample_2',
              title: '学习React和TypeScript',
              completed: false,
              createdAt: new Date(),
              tags: [],
              priority: 'medium',
              estimatedPomodoros: 2
            }
          ];

          const completedSample: Task = {
            id: 'sample_completed_1',
            title: '设置开发环境',
            completed: true,
            createdAt: new Date(Date.now() - 86400000), // 昨天
            completedAt: new Date(Date.now() - 3600000), // 1小时前
            tags: [],
            priority: 'low',
            actualPomodoros: 1
          };

          // 为示例已完成任务生成历史记录
          const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
          const sampleTaskHistory = [
            { date: yesterday, count: 1 }
          ];

          set({
            tasks: sampleTasks,
            completedTasks: [completedSample],
            taskHistory: sampleTaskHistory
          });
        }
      },
    }),
    {
      name: 'task-store',
    }
  )
);


// 这是一个辅助函数，用于在非React环境中使用store
export const getTaskStoreState = () => useTaskStore.getState();